import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Pagination from 'common-ui/table/Pagination';
import {
  createCompetitor,
  deleteCompetitor,
  getCompetitors,
  updateCompetitor,
} from 'features/newsales/newsalesActions';
import {
  selectCompetitors,
  selectCompetitorsCurrentPage,
  selectCompetitorsDeleteSuccess,
  selectCompetitorsLoading,
  selectCompetitorsPagesCount,
  selectCompetitorsSearchData,
  selectCompetitorsSuccess,
  selectCompetitorsUpdateSuccess,
  setCompetitorsCreateSuccess,
  setCompetitorsCurrentPage,
  setCompetitorsDeleteSuccess,
  setCompetitorsSearchData,
  setCompetitorsUpdateSuccess,
} from 'features/newsales/newsalesSlice';

import CompetitorCard from '../components/CompetitorCard';
import CompetitorModal from '../components/CompetitorModal';
import SearchFilter from '../components/SearchFilter';
import {
  CardsGrid,
  CompetitorContainer,
  CompetitorContent,
  CompetitorTitle,
  EmptyState,
  LoadingSpinner,
  PaginationWrapper,
} from './Competitor.styles';

const Competitor = () => {
  const dispatch = useDispatch();

  const competitors = useSelector(selectCompetitors);
  const loading = useSelector(selectCompetitorsLoading);
  const currentPage = useSelector(selectCompetitorsCurrentPage);
  const pagesCount = useSelector(selectCompetitorsPagesCount);
  const searchData = useSelector(selectCompetitorsSearchData);
  const createSuccess = useSelector(selectCompetitorsSuccess);
  const updateSuccess = useSelector(selectCompetitorsUpdateSuccess);
  const deleteSuccess = useSelector(selectCompetitorsDeleteSuccess);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);

  useEffect(() => {
    const params = {
      status: searchData.status,
      page: currentPage,
      limit: searchData.limit,
      name: searchData.search,
    };
    dispatch(getCompetitors(params));
  }, [dispatch, currentPage, searchData.status, searchData.limit, searchData.search]);

  useEffect(() => {
    if (createSuccess) {
      setIsModalOpen(false);
      setSelectedCompetitor(null);
      dispatch(setCompetitorsCreateSuccess(false));
      const params = {
        status: searchData.status,
        page: currentPage,
        limit: searchData.limit,
        name: searchData.search,
      };
      dispatch(getCompetitors(params));
    }
  }, [createSuccess, dispatch, currentPage, searchData]);

  useEffect(() => {
    if (updateSuccess) {
      setIsModalOpen(false);
      setSelectedCompetitor(null);
      dispatch(setCompetitorsUpdateSuccess(false));
      const params = {
        status: searchData.status,
        page: currentPage,
        limit: searchData.limit,
        search: searchData.search,
      };
      dispatch(getCompetitors(params));
    }
  }, [updateSuccess, dispatch, currentPage, searchData]);

  useEffect(() => {
    if (deleteSuccess) {
      dispatch(setCompetitorsDeleteSuccess(false));

      // If deleting the last item on a page (and not on page 1), go to previous page
      const shouldGoToPreviousPage = competitors?.length === 1 && currentPage > 1;

      if (shouldGoToPreviousPage) {
        dispatch(setCompetitorsCurrentPage(currentPage - 1));
      } else {
        // Refetch current page
        const params = {
          status: searchData.status,
          page: currentPage,
          limit: searchData.limit,
          search: searchData.search,
        };
        dispatch(getCompetitors(params));
      }
    }
  }, [deleteSuccess, dispatch, competitors?.length, currentPage, searchData]);

  const handleSearchChange = (value) => {
    dispatch(setCompetitorsSearchData({ search: value }));
    dispatch(setCompetitorsCurrentPage(1));
  };

  const handleStatusChange = (value) => {
    dispatch(setCompetitorsSearchData({ status: value }));
    dispatch(setCompetitorsCurrentPage(1));
  };

  const handlePageChange = (page) => {
    dispatch(setCompetitorsCurrentPage(page));
  };

  const handleRowCountChange = (count) => {
    dispatch(setCompetitorsSearchData({ limit: count }));
    dispatch(setCompetitorsCurrentPage(1));
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setSelectedCompetitor(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (id) => {
    const competitorToEdit = competitors.find((competitor) => competitor.id === id);
    if (competitorToEdit) {
      setModalMode('edit');
      setSelectedCompetitor(competitorToEdit);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCompetitor(null);
  };

  const handleSubmit = (idOrData, competitorData) => {
    if (modalMode === 'create') {
      dispatch(createCompetitor(idOrData));
    } else {
      dispatch(updateCompetitor({ id: idOrData, data: competitorData }));
    }
  };

  const handleDeleteCompetitor = (id) => {
    dispatch(deleteCompetitor(id));
  };

  return (
    <CompetitorContainer>
      <CompetitorTitle>Competitors</CompetitorTitle>
      <SearchFilter
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        searchPlaceholder="Search competitors..."
        showCreateButton
        onCreateClick={handleOpenCreateModal}
        createButtonText="+ Create Competitor"
      />
      <CompetitorContent>
        {loading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : !competitors || competitors?.length === 0 ? (
          <EmptyState>No competitors found</EmptyState>
        ) : (
          <>
            <CardsGrid>
              {competitors.map((competitor) => (
                <CompetitorCard
                  key={competitor.id}
                  name={competitor.name}
                  description={competitor.description}
                  image={competitor.image}
                  enabled={competitor.enabled}
                  onEdit={() => handleOpenEditModal(competitor.id)}
                  onDelete={() => handleDeleteCompetitor(competitor.id)}
                />
              ))}
            </CardsGrid>

            {pagesCount > 1 && (
              <PaginationWrapper>
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagesCount}
                  onPageChange={handlePageChange}
                  handleRowCountChange={handleRowCountChange}
                  dataCount={competitors?.length}
                  count={searchData.limit}
                  editableRowCount
                />
              </PaginationWrapper>
            )}
          </>
        )}
      </CompetitorContent>

      <CompetitorModal
        mode={modalMode}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onCreate={handleSubmit}
        onUpdate={handleSubmit}
        initialData={selectedCompetitor}
      />
    </CompetitorContainer>
  );
};

export default Competitor;
